import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eraser } from "lucide-react";

/**
 * On-screen signature pad. Returns base64 PNG via onChange.
 */
export default function SignaturePad({ label, onChange, value, testId = "signature-pad" }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const ratio = window.devicePixelRatio || 1;
    const w = c.clientWidth;
    const h = c.clientHeight;
    c.width = w * ratio;
    c.height = h * ratio;
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#020617";
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, w, h);
        setIsEmpty(false);
      };
      img.src = value;
    }
  }, []);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - rect.left;
    const y = (t ? t.clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    setIsEmpty(false);
    const data = canvasRef.current.toDataURL("image/png");
    onChange?.(data);
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setIsEmpty(true);
    onChange?.(null);
  };

  return (
    <div className="w-full" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">{label}</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={clear}
          data-testid={`${testId}-clear`}
        >
          <Eraser className="w-3.5 h-3.5 mr-1" /> Limpar
        </Button>
      </div>
      <canvas
        ref={canvasRef}
        className="ag-signature-canvas border-2 border-dashed border-slate-300 bg-slate-50 rounded-sm w-full h-44 sm:h-48 block"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      {isEmpty && (
        <p className="text-xs text-slate-400 mt-1 italic">Assine no espaço acima</p>
      )}
    </div>
  );
}
